import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { Line, Bar, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import GoogleAnalytics from "./GoogleAnalytics";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);

const AdminDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    ReactGA.initialize("G-LVKSK2K992");
    ReactGA.event({
      category: "Admin Dashboard",
      action: "Page Loaded",
      label: "Admin page",
    });

    const fetchAnalyticsData = async () => {
      try {
        const response = await axios.get("https://cors-proxy-production-5aee.up.railway.app/api/analytics");
        setAnalyticsData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Analytics verisi çekme hatası:", err);
        setError("Veriler yüklenirken bir hata oluştu.");
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  if (loading) return <div className="text-center mt-5">Veriler yükleniyor...</div>;
  if (error) return <div className="text-center mt-5 text-danger">{error}</div>;
  if (analyticsData.length === 0) return <div className="text-center mt-5">Gösterilecek veri yok.</div>;

  // Tarih ve veri gruplama
  const dates = [...new Set(analyticsData.map(item => item.date))].sort();
  const activeUsersByDate = dates.map(date => 
    analyticsData.filter(item => item.date === date).reduce((sum, item) => sum + parseInt(item.activeUsers), 0)
  );
  const pageViewsByDate = dates.map(date => 
    analyticsData.filter(item => item.date === date).reduce((sum, item) => sum + parseInt(item.pageViews), 0)
  );
  const sessionsByDate = dates.map(date => 
    analyticsData.filter(item => item.date === date).reduce((sum, item) => sum + parseInt(item.sessions), 0)
  );
  const bounceRateByDate = dates.map(date => {
    const totalBounce = analyticsData.filter(item => item.date === date).reduce((sum, item) => sum + parseFloat(item.bounceRate), 0);
    const count = analyticsData.filter(item => item.date === date).length;
    return count ? (totalBounce / count).toFixed(1) : 0; // Ortalama bounce rate
  });

  // Ülke bazlı veri
  const countries = [...new Set(analyticsData.map(item => item.country))];
  const pageViewsByCountry = countries.map(country => 
    analyticsData.filter(item => item.country === country).reduce((sum, item) => sum + parseInt(item.pageViews), 0)
  );

  // Grafik verileri
  const activeUsersData = {
    labels: dates.map(date => `${date.slice(6, 8)}.${date.slice(4, 6)}.${date.slice(0, 4)}`),
    datasets: [
      {
        label: "Günlük Aktif Kullanıcılar",
        data: activeUsersByDate,
        fill: true,
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2,
        tension: 0.3,
      },
    ],
  };

  const pageViewsData = {
    labels: dates.map(date => `${date.slice(6, 8)}.${date.slice(4, 6)}.${date.slice(0, 4)}`),
    datasets: [
      {
        label: "Günlük Sayfa Görüntülemeleri",
        data: pageViewsByDate,
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
    ],
  };

  const sessionsData = {
    labels: dates.map(date => `${date.slice(6, 8)}.${date.slice(4, 6)}.${date.slice(0, 4)}`),
    datasets: [
      {
        label: "Günlük Oturumlar",
        data: sessionsByDate,
        fill: true,
        backgroundColor: "rgba(255, 159, 64, 0.2)",
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 2,
        tension: 0.3,
      },
    ],
  };

  const bounceRateData = {
    labels: dates.map(date => `${date.slice(6, 8)}.${date.slice(4, 6)}.${date.slice(0, 4)}`),
    datasets: [
      {
        label: "Hemen Çıkma Oranı (%)",
        data: bounceRateByDate,
        backgroundColor: "rgba(255, 99, 132, 0.6)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
    ],
  };

  const countryData = {
    labels: countries,
    datasets: [
      {
        label: "Ülkelere Göre Sayfa Görüntülemeleri",
        data: pageViewsByCountry,
        backgroundColor: ["#ff6384", "#36a2eb", "#ffce56", "#4bc0c0", "#9966ff", "#ff9f40"],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { font: { size: 14 } } },
      tooltip: { backgroundColor: "rgba(0, 0, 0, 0.8)", titleFont: { size: 16 }, bodyFont: { size: 14 } },
      title: { display: true, font: { size: 18 }, padding: 20 },
    },
    scales: {
      x: { grid: { display: false }, title: { display: true, text: "Tarih", font: { size: 14 } } },
      y: { beginAtZero: true, title: { display: true, text: "Değer", font: { size: 14 } } },
    },
  };

  return (
    <>
      <GoogleAnalytics />
      <div className="container mt-4">
        <h1 className="mb-4 text-center">Analytics Dashboard</h1>
        <div className="row">
          <div className="col-md-6 mb-4">
            <div style={{ height: "400px" }}>
              <Line
                data={activeUsersData}
                options={{ ...chartOptions, plugins: { ...chartOptions.plugins, title: { text: "Günlük Aktif Kullanıcılar" } } }}
              />
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div style={{ height: "400px" }}>
              <Bar
                data={pageViewsData}
                options={{ ...chartOptions, plugins: { ...chartOptions.plugins, title: { text: "Günlük Sayfa Görüntülemeleri" } } }}
              />
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div style={{ height: "400px" }}>
              <Line
                data={sessionsData}
                options={{ ...chartOptions, plugins: { ...chartOptions.plugins, title: { text: "Günlük Oturumlar" } } }}
              />
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div style={{ height: "400px" }}>
              <Bar
                data={bounceRateData}
                options={{ ...chartOptions, plugins: { ...chartOptions.plugins, title: { text: "Hemen Çıkma Oranı (%)" } } }}
              />
            </div>
          </div>
          <div className="col-md-6 mb-4">
            <div style={{ height: "400px" }}>
              <Pie
                data={countryData}
                options={{ ...chartOptions, plugins: { ...chartOptions.plugins, title: { text: "Ülkelere Göre Sayfa Görüntülemeleri" } } }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;